"use client";

import {DownloadIcon} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {formatTimeWithoutSecods} from "@/libs/helper/util";
import {userService} from "@/service/users.service";
import {Spinner} from "@nextui-org/react";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader
} from "@react-google-maps/api";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {FiMapPin} from "react-icons/fi";
import {MdDirectionsBus, MdRoute, MdStraighten} from "react-icons/md";

const mapKey: string = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";

const mapStyles = [
  {
    featureType: "all",
    elementType: "labels.text.fill",
    stylers: [{color: "#7c93a3"}, {lightness: "-10"}]
  },
  {
    featureType: "administrative.country",
    elementType: "geometry",
    stylers: [{visibility: "on"}]
  },
  {
    featureType: "administrative.country",
    elementType: "geometry.stroke",
    stylers: [{color: "#c2d1d6"}]
  },
  {
    featureType: "landscape",
    elementType: "geometry.fill",
    stylers: [{color: "#dde3e3"}]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.fill",
    stylers: [{color: "#c2d1d6"}]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{color: "#a9b4b8"}, {lightness: "0"}]
  },
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{color: "#a3c7df"}]
  }
];

type BpEntry = {
  id: string;
  type: "pickup" | "drop";
  vehicleName: string;
  routeName: string;
  boardingPoint: string;
  lat: number;
  lng: number;
  isActive: boolean;
  refNo: number;
  boardingTime: string;
  updatedDatetime: number | null;
};

type FilterTab = "all" | "current" | "history";

type BpHistoryProps = {
  selectedRow?: any;
  onClose?: () => void;
};

const FILTER_LABELS: Record<FilterTab, string> = {
  all: "All",
  current: "Current",
  history: "History"
};

const FILTER_ACTIVE_CLS: Record<FilterTab, string> = {
  all: "border-[#FBC02D] text-black",
  current: "border-[#009759] text-black",
  history: "border-default-400 text-black"
};

const FILTER_COUNT_CLS: Record<FilterTab, string> = {
  all: "bg-[#FBC02D] text-white",
  current: "bg-[#009759] text-white",
  history: "bg-default-400 text-white"
};

const FILTER_COUNT_ACTIVE_CLS: Record<FilterTab, string> = {
  all: "bg-[#FBC02D] text-white",
  current: "bg-[#009759] text-white",
  history: "bg-default-400 text-white"
};

const containerStyle = {width: "100%", height: "100%"};

export const BpHistory: React.FC<BpHistoryProps> = ({selectedRow}) => {
  const {filterContext} = useNeoContext();

  const [allEntries, setAllEntries] = useState<BpEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<{
    name: string;
    admissionNo: string;
    className: string;
  } | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");
  const [showAll, setShowAll] = useState(false);
  const [activeCollapsed, setActiveCollapsed] = useState(false);
  const [historyCollapsed, setHistoryCollapsed] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [tooltipEntryId, setTooltipEntryId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState({lat: 11.0168, lng: 76.9558});

  const mapRef = useRef<google.maps.Map | null>(null);

  const {isLoaded} = useJsApiLoader({googleMapsApiKey: mapKey});

  const userId: string = selectedRow?.id ?? selectedRow?.userId ?? "";

  const schoolLat = Number(filterContext?.client?.insLat);
  const schoolLng = Number(filterContext?.client?.insLng);

  const DEFAULT_CENTER = useMemo(
    () =>
      Number.isFinite(schoolLat) &&
      Number.isFinite(schoolLng) &&
      schoolLat !== 0 &&
      schoolLng !== 0
        ? {lat: schoolLat, lng: schoolLng}
        : {lat: 11.0168, lng: 76.9558},
    [schoolLat, schoolLng]
  );

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const fetchHistory = async () => {
      setLoading(true);

      try {
        const result = await userService.onGetBpHistory(userId);

        if (cancelled) return;

        const {pickup, drop} = result;

        const pickupEntries: BpEntry[] = pickup.map(
          (item: any, idx: number) => ({
            id: `pickup-${idx}`,
            type: "pickup",
            vehicleName: item.vehicleName ?? "N/A",
            routeName: item.routeName ?? "N/A",
            boardingPoint: item.bpName || "N/A",
            lat: Number(item.bpLat ?? 0),
            lng: Number(item.bpLng ?? 0),
            isActive: !!item.activeBp,
            refNo: idx + 1,
            boardingTime: item.refBpDatetime
              ? formatTimeWithoutSecods(Number(item.refBpDatetime))
              : "",
            updatedDatetime: item.updatedDatetime
              ? Number(item.updatedDatetime)
              : null
          })
        );

        const dropEntries: BpEntry[] = drop.map((item: any, idx: number) => ({
          id: `drop-${idx}`,
          type: "drop",
          vehicleName: item.vehicleName ?? "",
          routeName: item.routeName ?? "",
          boardingPoint: item.bpName || "",
          lat: Number(item.bpLat ?? 0),
          lng: Number(item.bpLng ?? 0),
          isActive: !!item.activeBp,
          refNo: idx + 1,
          boardingTime: item.refBpDatetime
            ? formatTimeWithoutSecods(Number(item.refBpDatetime))
            : "",
          updatedDatetime: item.updatedDatetime
            ? Number(item.updatedDatetime)
            : null
        }));

        const entries = [...pickupEntries, ...dropEntries];

        if (cancelled) return;

        setAllEntries(entries);

        const first = entries.find(e => e.lat !== 0 && e.lng !== 0);

        setMapCenter(first ? {lat: first.lat, lng: first.lng} : DEFAULT_CENTER);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchHistory();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    userService.onFetchUserDetails(userId, "edit", "user").then((res: any) => {
      const d = res?.data ?? res;
      setUserInfo({
        name: d?.name ?? "",
        admissionNo: d?.admissionNo ?? "",
        className: d?.className ?? ""
      });
    });
  }, [userId]);

  const filteredEntries = useMemo(() => {
    switch (activeFilter) {
      case "current":
        return allEntries.filter(e => e.isActive);
      case "history":
        return allEntries.filter(e => !e.isActive);
      default:
        return allEntries;
    }
  }, [allEntries, activeFilter]);

  // Cards in left panel follow the filter; map markers follow showAll toggle
  const displayEntries = filteredEntries;
  const mapEntries = showAll ? allEntries : filteredEntries;

  const counts = useMemo(
    () => ({
      all: allEntries.length,
      current: allEntries.filter(e => e.isActive).length,
      history: allEntries.filter(e => !e.isActive).length
    }),
    [allEntries]
  );

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      map.setCenter(mapCenter);
    },
    [mapCenter]
  );

  const handleCardSelect = (entry: BpEntry) => {
    setSelectedEntryId(entry.id);
    if (entry.lat !== 0 && entry.lng !== 0) {
      const pos = {lat: entry.lat, lng: entry.lng};
      setMapCenter(pos);
      if (mapRef.current) {
        mapRef.current.panTo(pos);
        mapRef.current.setZoom(21);
      }
    }
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white">
      {/* Header */}
      <div className="flex flex-shrink-0 items-center gap-0 border-b border-default-100 bg-white py-3 pl-5">
        <div className="flex flex-row items-center gap-36">
          <span className="text-lg font-medium leading-tight">
            Boarding History
          </span>
          <div>
            <button
              onClick={async () => {
                try {
                  const {utils, writeFileXLSX} = await import("xlsx");
                  const fileName = `BP-History-${userInfo?.name ?? "export"}-${new Date().toISOString().slice(0, 10)}.xlsx`;
                  const header = [
                    "S.No",
                    "Vehicle",
                    "Route",
                    "Boarding Point",
                    "Trip Type",
                    "Boarding Time",
                    "Updated Time",
                    "Status"
                  ];
                  const rows = allEntries.map((e, i) => [
                    i + 1,
                    e.vehicleName || "",
                    e.routeName || "",
                    e.boardingPoint || "",
                    e.type === "pickup" ? "Pickup" : "Drop",
                    e.boardingTime || "",
                    new Date().toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true
                    }),
                    e.isActive ? "Current" : "History"
                  ]);
                  const worksheet = utils.aoa_to_sheet([header, ...rows]);
                  const workbook = utils.book_new();
                  utils.book_append_sheet(workbook, worksheet, "BP History");
                  writeFileXLSX(workbook, fileName);
                } catch {
                  console.error("Failed to export Excel file");
                }
              }}
              className="flex-shrink-0 rounded-lg p-1.5 text-default-500 hover:bg-default-100 hover:text-default-700"
              title="Download history"
            >
              <DownloadIcon />
            </button>
          </div>
        </div>

        {/* Filter tabs + user info */}
        <div className="ml-3 mr-14 flex flex-1 items-center justify-between">
          <div className="flex items-center gap-1">
            {(["all", "current", "history"] as FilterTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveFilter(tab);
                  setActiveCollapsed(false);
                  setHistoryCollapsed(false);
                }}
                className={`flex items-center gap-1 rounded-full border-2 px-3 py-1 text-xs font-medium transition-colors ${
                  activeFilter === tab
                    ? FILTER_ACTIVE_CLS[tab]
                    : "hover:bg-default-100"
                }`}
              >
                <span>{FILTER_LABELS[tab]}</span>
                <span
                  className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                    activeFilter === tab
                      ? FILTER_COUNT_ACTIVE_CLS[tab]
                      : FILTER_COUNT_CLS[tab]
                  }`}
                >
                  {counts[tab]}
                </span>
              </button>
            ))}
          </div>

          {userInfo && (
            <div className="flex items-center gap-2">
              <div className="group relative flex items-center gap-2 rounded-xl border border-default-200 bg-default-50 px-3 py-2 shadow-sm">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                  {userInfo.name
                    ? userInfo.name.slice(0, 2).toUpperCase()
                    : "??"}
                </div>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-semibold leading-tight text-default-800">
                    {userInfo.name || "N/A"}
                  </span>
                  <div className="flex items-center gap-2 text-xs leading-none text-black">
                    {userInfo.admissionNo && (
                      <span>
                        <span className="text-default-500">Admission No: </span>
                        {userInfo.admissionNo.length > 15
                          ? `${userInfo.admissionNo.slice(0, 15)}...`
                          : userInfo.admissionNo}
                      </span>
                    )}
                  </div>
                </div>
                {/* Hover tooltip */}
                <div className="pointer-events-none absolute left-0 top-full z-50 mt-1.5 hidden w-full rounded-lg border border-default-200 bg-white px-3 py-2.5 shadow-lg group-hover:block">
                  <div className="flex flex-col gap-1 text-xs">
                    <div>
                      <span className="text-default-500">Name: </span>
                      <span className="font-semibold text-default-800">
                        {userInfo.name || "N/A"}
                      </span>
                    </div>
                    {userInfo.admissionNo && (
                      <div>
                        <span className="text-default-500">Admission No: </span>
                        <span className="font-semibold text-default-800">
                          {userInfo.admissionNo}
                        </span>
                      </div>
                    )}
                    {userInfo.className && (
                      <div>
                        <span className="text-default-500">Class: </span>
                        <span className="font-semibold text-default-800">
                          {userInfo.className}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1">
        {/* Left panel */}
        <div className="flex w-[340px] flex-shrink-0 flex-col overflow-y-auto border-r border-default-100 bg-default-50">
          {loading ? (
            <div className="flex flex-1 items-center justify-center py-10">
              <Spinner size="sm" />
            </div>
          ) : (
            (() => {
              const activeEntries = allEntries.filter(e => e.isActive);
              const historyEntries = allEntries.filter(e => !e.isActive);
              const showCurrent =
                activeFilter === "all" || activeFilter === "current";
              const showHistory =
                activeFilter === "all" || activeFilter === "history";
              if (
                activeFilter === "all" &&
                activeEntries.length === 0 &&
                historyEntries.length === 0
              ) {
                return (
                  <div className="flex flex-1 items-center justify-center py-10 text-default-600">
                    No boarding history found
                  </div>
                );
              }
              return (
                <div className="mr-1 flex flex-col gap-1">
                  {showCurrent && (
                    <>
                      <button
                        onClick={() =>
                          activeEntries.length > 0 &&
                          setActiveCollapsed(v => !v)
                        }
                        className="sticky top-0 z-10 flex w-full items-center gap-2 rounded-md px-3 py-2 text-left"
                        style={{backgroundColor: "#D6F0E4"}}
                      >
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{backgroundColor: "#009759"}}
                        />
                        <span className="text-xs font-semibold uppercase tracking-wider">
                          Current
                        </span>
                        <span
                          className="rounded-full px-1.5 py-0.5 text-xs font-semibold text-white"
                          style={{backgroundColor: "#009759"}}
                        >
                          {activeEntries.length}
                        </span>
                        <span className="ml-auto" />
                        {activeEntries.length > 0 && (
                          <svg
                            className={`h-4 w-4 transition-transform ${activeCollapsed ? "-rotate-90" : "rotate-0"}`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        )}
                      </button>
                      {!activeCollapsed &&
                        activeEntries.map(entry => (
                          <EntryCard
                            key={entry.id}
                            entry={entry}
                            isSelected={selectedEntryId === entry.id}
                            onSelect={() => handleCardSelect(entry)}
                          />
                        ))}
                    </>
                  )}
                  {showHistory && (
                    <>
                      <button
                        onClick={() =>
                          historyEntries.length > 0 &&
                          setHistoryCollapsed(v => !v)
                        }
                        className="sticky top-0 z-10 mr-4 flex w-full items-center gap-2 rounded-md bg-gray-200 px-3 py-2 text-left"
                      >
                        <span className="h-2 w-2 rounded-full bg-default-400" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-default-500">
                          History
                        </span>
                        <span className="rounded-full bg-default-400 px-1.5 py-0.5 text-xs font-semibold text-white">
                          {historyEntries.length}
                        </span>
                        <span className="ml-auto" />
                        {historyEntries.length > 0 && (
                          <svg
                            className={`h-4 w-4 text-default-500 transition-transform ${historyCollapsed ? "-rotate-90" : "rotate-0"}`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        )}
                      </button>
                      {!historyCollapsed &&
                        historyEntries.map(entry => (
                          <EntryCard
                            key={entry.id}
                            entry={entry}
                            isSelected={selectedEntryId === entry.id}
                            onSelect={() => handleCardSelect(entry)}
                          />
                        ))}
                    </>
                  )}
                </div>
              );
            })()
          )}
        </div>

        {/* Map panel */}
        <div className="relative flex-1">
          {!isLoaded ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size="sm" />
            </div>
          ) : (
            <>
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter}
                zoom={12}
                onLoad={onMapLoad}
                options={{
                  styles: mapStyles,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  gestureHandling: "greedy",
                  scrollwheel: true,
                  zoomControl: true,
                  zoomControlOptions: {
                    position: window.google?.maps?.ControlPosition?.RIGHT_CENTER
                  }
                }}
              >
                {(() => {
                  const OFFSET = 0.00002;
                  const seen = new Map<string, number>();
                  return mapEntries.map(entry => {
                    if (entry.lat === 0 && entry.lng === 0) return null;
                    const key = `${entry.lat.toFixed(6)},${entry.lng.toFixed(6)}`;
                    const idx = seen.get(key) ?? 0;
                    seen.set(key, idx + 1);
                    const angle = (idx * 90 * Math.PI) / 180;
                    const spread = idx === 0 ? 0 : OFFSET;
                    const lat = entry.lat + spread * Math.cos(angle);
                    const lng = entry.lng + spread * Math.sin(angle);
                    const isPickup = entry.type === "pickup";
                    const markerColor = isPickup ? "#2563EB" : "#8A5600";
                    const showTooltip = tooltipEntryId === entry.id;
                    return (
                      <Marker
                        key={entry.id}
                        position={{lat, lng}}
                        label={{
                          text: String(entry.refNo),
                          color: "#ffffff",
                          fontSize: "11px",
                          fontWeight: "bold"
                        }}
                        icon={{
                          path: window.google?.maps?.SymbolPath?.CIRCLE,
                          scale: 16,
                          fillColor: markerColor,
                          fillOpacity: 1,
                          strokeColor: "#ffffff",
                          strokeWeight: 2
                        }}
                        onClick={() => {
                          handleCardSelect(entry);
                          setTooltipEntryId(showTooltip ? null : entry.id);
                        }}
                        onMouseOver={() => setTooltipEntryId(entry.id)}
                        onMouseOut={() => setTooltipEntryId(null)}
                      >
                        {showTooltip && (
                          <InfoWindow
                            position={{lat: entry.lat, lng: entry.lng}}
                            options={{
                              disableAutoPan: true,
                              pixelOffset: new window.google.maps.Size(0, -20)
                            }}
                            onCloseClick={() => setTooltipEntryId(null)}
                          >
                            <div
                              style={{
                                fontFamily: "sans-serif",
                                fontSize: 12,
                                minWidth: 160,
                                lineHeight: 1.6
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 700,
                                  marginBottom: 4,
                                  color: isPickup ? "#3b82f6" : "#f97316"
                                }}
                              >
                                {isPickup ? "Pickup" : "Drop"} #{entry.refNo}
                                {entry.isActive && (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      color: "#22c55e",
                                      fontWeight: 600
                                    }}
                                  >
                                    ● Current
                                  </span>
                                )}
                              </div>
                              <div style={{marginBottom: 2, fontSize: 12}}>
                                <span style={{color: "#6b7280"}}>
                                  Vehicle:{" "}
                                </span>
                                <span style={{fontWeight: 400}}>
                                  {entry.vehicleName || "N/A"}
                                </span>
                              </div>
                              <div style={{marginBottom: 2, fontSize: 12}}>
                                <span style={{color: "#6b7280"}}>Route: </span>
                                <span style={{fontWeight: 400}}>
                                  {entry.routeName || "N/A"}
                                </span>
                              </div>
                              <div style={{marginBottom: 2, fontSize: 12}}>
                                <span style={{color: "#6b7280"}}>
                                  Boarding:{" "}
                                </span>
                                <span style={{fontWeight: 400}}>
                                  {entry.boardingPoint || "N/A"}
                                </span>
                              </div>
                              <div style={{marginBottom: 2, fontSize: 12}}>
                                <span style={{color: "#6b7280"}}>
                                  Stop Time:{" "}
                                </span>
                                <span style={{fontWeight: 400}}>
                                  {entry.boardingTime || "N/A"}
                                </span>
                              </div>
                              <div style={{fontSize: 12}}>
                                <span style={{color: "#6b7280"}}>
                                  Updated on:{" "}
                                </span>
                                <span style={{fontWeight: 400}}>
                                  {entry.updatedDatetime
                                    ? new Date(
                                        entry.updatedDatetime
                                      ).toLocaleString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true
                                      })
                                    : "N/A"}
                                </span>
                              </div>
                            </div>
                          </InfoWindow>
                        )}
                      </Marker>
                    );
                  });
                })()}

                {/* School marker */}
                {Number.isFinite(schoolLat) &&
                  Number.isFinite(schoolLng) &&
                  schoolLat !== 0 &&
                  schoolLng !== 0 && (
                    <Marker
                      position={{lat: schoolLat, lng: schoolLng}}
                      title={filterContext?.client?.institutionName || "School"}
                      icon={{
                        url: "/images/school_3.png",
                        scaledSize: new window.google.maps.Size(40, 40)
                      }}
                    />
                  )}
              </GoogleMap>

              {/* Legend */}
              <div className="absolute left-4 top-4 rounded-lg border border-default-100 bg-white px-4 py-3 shadow-md">
                <p className="mb-2 text-xs font-semibold text-default-700">
                  Legend
                </p>
                <div className="flex flex-col gap-1.5">
                  <LegendItem color="#2563EB" label="Pickup" />
                  <LegendItem color="#8A5600" label="Drop" />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

type EntryCardProps = {
  entry: BpEntry;
  isSelected: boolean;
  onSelect: () => void;
};

const EntryCard: React.FC<EntryCardProps> = ({entry, isSelected, onSelect}) => {
  const isPickup = entry.type === "pickup";

  const accentBg = isPickup
    ? "border text-[#2563EB] bg-[#EAF2FF] border-[#2563EB]/30"
    : "border text-[#8A5600] bg-[#FFF4E5] border-[#8A5600]/30";
  const badgeBgColor = isPickup ? "#2563EB" : "#8A5600";

  return (
    <div onClick={onSelect} className="cursor-pointer px-2.5 py-1">
      <div
        className={`rounded-xl border bg-white shadow-sm transition-all ${
          isSelected ? "border-2 shadow-md" : "border-2 border-default-200"
        }`}
        style={isSelected ? {borderColor: "#FBC02D"} : {}}
      >
        {/* Card header */}
        <div className="flex items-start justify-between px-4 py-3">
          <div className="flex items-start gap-2.5">
            <div
              className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
              style={{backgroundColor: badgeBgColor}}
            >
              {entry.refNo}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold leading-tight text-default-800">
                {entry.boardingPoint || "N/A"}
              </span>
              <span className="text-xs text-default-600">
                Stop time : {entry.boardingTime || "N/A"}
              </span>
            </div>
          </div>
          <span
            className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${accentBg}`}
          >
            {isPickup ? "Pickup" : "Drop"}
          </span>
        </div>

        {/* Detail rows */}
        <div className="flex flex-col gap-1.5 border-t border-default-200 px-4 py-3">
          <RouteDetailRow
            icon="bus"
            label="Vehicle"
            value={entry.vehicleName || "N/A"}
          />
          <RouteDetailRow
            icon="route"
            label="Route"
            value={entry.routeName || "N/A"}
          />
        </div>

        {/* Updated on footer */}
        <div className="flex items-center gap-1.5 border-t border-default-200 px-4 py-2">
          <svg
            className="h-3 w-3 flex-shrink-0 text-default-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span className="text-xs text-default-600">
            {"Updated on "}
            {entry.updatedDatetime
              ? new Date(entry.updatedDatetime).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })
              : "N/A"}
            {entry.updatedDatetime ? (
              <>
                ,{"  "}
                {new Date(entry.updatedDatetime).toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true
                })}
              </>
            ) : null}
          </span>
        </div>
      </div>
    </div>
  );
};

type RouteDetailRowProps = {
  icon: "bus" | "route" | "pin" | "time";
  label: string;
  value: string;
};

const RouteDetailRow: React.FC<RouteDetailRowProps> = ({
  icon,
  label,
  value
}) => {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-default-600">
        {icon === "bus" ? (
          <MdDirectionsBus size={12} />
        ) : icon === "route" ? (
          <MdRoute size={12} />
        ) : icon === "time" ? (
          <MdStraighten size={12} />
        ) : (
          <FiMapPin size={12} />
        )}
      </span>
      <span className="w-[52px] flex-shrink-0 text-xs  text-gray-500 ">
        {label}
      </span>
      <span className="text-xs font-medium">{value}</span>
    </div>
  );
};

type LegendItemProps = {
  color: string;
  label: string;
};

const LegendItem: React.FC<LegendItemProps> = ({color, label}) => {
  return (
    <div className="flex items-center gap-2">
      <span className="h-2 w-2 rounded-full" style={{backgroundColor: color}} />
      <span className="text-xs text-default-600">{label}</span>
    </div>
  );
};
